import {
  type StepBuildCeremony,
  mergeStepBuildProps,
  pageBodyBlockBuildProps,
  sectionPageIntroMotions,
} from "@/components/benefits-discovery-v2/step-sequential-build";
import { useCountUp } from "@/components/benefits-discovery-v2/use-count-up";
import { cn } from "@/lib/utils";
import { CheckCircle2, Clock, Users } from "@cakewalk/ui/icons";
import {
  V2Button,
  V2Card,
  V2CardContent,
  V2EnrollmentFlowMain,
  V2Typography,
} from "@cakewalk/ui/v2";
import type { BusinessInfo, TeamMember } from "./types";

/**
 * Step 5 of the company-census flow: confirmation. Deliberately quote-free —
 * we thank the client, confirm what we received, and set the expectation that
 * we'll follow up with their medical quote. (The demo's employee-invite
 * summary was removed along with the "ask my team" path.)
 */

export function CompanyCensusCompleteStep({
  business,
  members,
  playBuild = false,
  ceremony = null,
  onBack,
}: {
  business: BusinessInfo;
  members: TeamMember[];
  playBuild?: boolean;
  ceremony?: StepBuildCeremony | null;
  onBack: () => void;
}) {
  const peopleCount = members.length;
  const dependentCount = members.reduce((sum, m) => sum + m.dependents.length, 0);
  const coveredLives = peopleCount + dependentCount;

  const peopleCountUp = useCountUp(peopleCount, { enabled: playBuild, durationMs: 1000 });
  // Restrained statement line — the covered-lives number is the quiet payoff of
  // the flow (§2.3). It rides the page-intro `cw-blur-in-up` rather than taking
  // over the canvas: a calm count-up settling into one sentence, then the
  // summary builds below.
  const coveredLivesCountUp = useCountUp(coveredLives, { enabled: playBuild, durationMs: 1400 });

  const { eyebrowMotion, titleMotion, descriptionMotion } = sectionPageIntroMotions(
    playBuild,
    ceremony,
  );

  return (
    <V2EnrollmentFlowMain width="default">
      <div className="mx-auto w-full max-w-cw-v2-content-narrow py-12 sm:py-16">
        <div className="flex flex-col items-center gap-3 text-center">
          <span
            className={cn(
              "flex h-16 w-16 items-center justify-center rounded-full bg-cw-v2-green-100 text-cw-v2-green-600",
              playBuild && "bd-v2-pop-in",
            )}
            aria-hidden="true"
          >
            <CheckCircle2 className="h-8 w-8" />
          </span>
          <V2Typography
            variant="overline"
            {...mergeStepBuildProps(eyebrowMotion, { className: "text-cw-v2-reliant-red" })}
          >
            All set
          </V2Typography>
          <V2Typography variant="display" as="h1" {...mergeStepBuildProps(titleMotion)}>
            Thank you — we&rsquo;ll take it from here
          </V2Typography>
          <V2Typography
            variant="body-large"
            muted
            {...mergeStepBuildProps(descriptionMotion, { className: "max-w-xl" })}
          >
            We&rsquo;ve got everything we need from {business.name.trim() || "your business"}. Our
            team will put together your medical quote and reach out with next steps. No action
            needed from you right now.
          </V2Typography>

          <V2Typography
            variant="heading-medium"
            as="p"
            {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 0, ceremony), {
              className: "mt-2 text-cw-v2-ink",
            })}
          >
            That&rsquo;s{" "}
            <span className="font-bold tabular-nums text-cw-v2-accent">{coveredLivesCountUp}</span>{" "}
            {coveredLives === 1 ? "person" : "people"} covered.
          </V2Typography>
        </div>

        <V2Card
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 1, ceremony), {
            className: "mt-8 overflow-hidden",
          })}
        >
          <V2CardContent className="flex flex-col gap-4 p-6 sm:p-8">
            <V2Typography variant="heading-small" as="h2">
              What we received
            </V2Typography>
            <div className="flex flex-col divide-y divide-cw-v2-border/60">
              <SummaryRow
                icon={<Users className="h-4 w-4" />}
                label="People to cover"
                value={`${peopleCountUp} ${peopleCount === 1 ? "employee" : "employees"}`}
              />
              <SummaryRow
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Dependents added"
                value={
                  dependentCount > 0
                    ? `${dependentCount} ${dependentCount === 1 ? "dependent" : "dependents"}`
                    : "None added"
                }
              />
            </div>
          </V2CardContent>
        </V2Card>

        <V2Card
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 2, ceremony), {
            className: "mt-4 overflow-hidden border-cw-v2-blue-200 bg-cw-v2-blue-50",
          })}
        >
          <V2CardContent className="flex items-start gap-3 p-6">
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cw-v2-blue-100 text-cw-v2-blue-600"
              aria-hidden="true"
            >
              <Clock className="h-4 w-4" />
            </span>
            <div className="flex flex-col gap-1">
              <V2Typography variant="body" className="font-semibold text-cw-v2-ink">
                What happens next
              </V2Typography>
              <V2Typography variant="body" muted>
                We&rsquo;ll review your team and prepare a medical quote tailored to your group.
                We&rsquo;ll email you as soon as it&rsquo;s ready.
              </V2Typography>
            </div>
          </V2CardContent>
        </V2Card>

        <div
          {...mergeStepBuildProps(pageBodyBlockBuildProps(playBuild, 3, ceremony), {
            className: "mt-8 flex flex-wrap items-center justify-center gap-1.5",
          })}
        >
          <V2Typography variant="body" muted>
            Need to update your roster?
          </V2Typography>
          <V2Button
            variant="link"
            size="md"
            onClick={onBack}
            data-track="button-company-census-complete-back"
          >
            Go back
          </V2Button>
        </div>
      </div>
    </V2EnrollmentFlowMain>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-cw-v2-md bg-cw-v2-blue-100 text-cw-v2-blue-600">
          {icon}
        </span>
        <V2Typography variant="body" muted>
          {label}
        </V2Typography>
      </div>
      <V2Typography variant="body" className="font-semibold text-cw-v2-ink">
        {value}
      </V2Typography>
    </div>
  );
}
